import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Loader } from "lucide-react";
import GoodsReceiptInvoice from "../components/inventory/GoodsReceiptInvoice";
import * as inventoryService from "../api/inventoryService";

export default function NotaReceiving() {
  const { id, rcvId } = useParams();
  const navigate = useNavigate();
  const [poData, setPoData] = useState(null);
  const [receivingData, setReceivingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const targetRcvId = rcvId || id;
        const targetPoId = rcvId ? id : null;

        // Fetch receiving detail
        const rcvRes = await inventoryService.getReceivingDetail(targetRcvId);
        const rcv = rcvRes?.data || rcvRes;
        if (!active) return;
        setReceivingData(rcv);

        // Fetch PO detail for complete supplier and PO info
        const poIdToFetch = targetPoId || rcv?.purchase_order_id;
        if (poIdToFetch) {
          try {
            const poRes = await inventoryService.getPurchaseOrderDetail(poIdToFetch);
            const po = poRes?.data || poRes;
            if (active) setPoData(po);
          } catch {
            // Ignore if PO detail fetch fails
          }
        }
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Gagal memuat data Bukti Penerimaan Barang");
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    if (id || rcvId) load();

    return () => {
      active = false;
    };
  }, [id, rcvId]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (id) {
      navigate(`/inventori/purchase-orders/${id}`);
    } else {
      navigate("/inventori/purchase-orders");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/80 py-0 print:bg-white print:p-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page {
            size: 210mm 148mm;
            margin: 4mm 6mm;
          }
          html, body {
            width: 210mm !important;
            height: 148mm !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Sticky Top Navigation Bar */}
      <div className="no-print sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-6 py-3.5 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors flex items-center gap-1.5 text-xs font-semibold shadow-2xs"
            title="Kembali">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
            <span>Kembali</span>
          </button>
          <div className="h-4 w-px bg-slate-200"></div>
          <div className="text-sm font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <span>Bukti Penerimaan Barang</span>
            {receivingData?.receiving_number && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-300 font-mono">
                {receivingData.receiving_number}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 shadow-sm shadow-teal-600/20 transition-all flex items-center gap-1.5">
            <Printer className="w-4 h-4" />
            <span>Cetak Bukti</span>
          </button>
        </div>
      </div>

      {/* Main Invoice Container */}
      <div className="p-4 sm:p-8 print:p-0 flex justify-center">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-16">
            <Loader className="w-5 h-5 animate-spin text-teal-600" />
            <span>Memuat data Bukti Penerimaan Barang...</span>
          </div>
        ) : error ? (
          <div className="p-6 bg-white rounded-2xl border border-rose-200 text-rose-600 text-sm max-w-md text-center my-12">
            {error}
          </div>
        ) : (
          <GoodsReceiptInvoice poData={poData} receivingData={receivingData} />
        )}
      </div>
    </div>
  );
}
